import {
  Component,
  Input,
  Output,
  EventEmitter,
  Injector,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import {
  getExpressionBuilderFormConfig,
  getExpressionTargetsForField,
  buildExpressionsFromVisualModel,
  parseExpressionToRules,
  IExpressionBuilderModel,
  IExpressionTarget,
} from '../../utils/expression-builder-configurations';
import { getAllServiceFormFieldsIncludingSelf } from '../../../../core/utils/form-fields-formly-configurations/conditional-settings-configurations';

@Component({
  selector: 'irembogov-fb-expression-editor',
  templateUrl: './fb-expression-editor.component.html',
  styleUrls: ['./fb-expression-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbExpressionEditorComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() field: FormlyFieldConfig | null = null;
  @Output() expressionsChanged = new EventEmitter<void>();
  @ViewChild('builderContainer', { static: false })
  builderContainer!: ElementRef;

  // Visual expression builder form
  builderForm = new FormGroup({});
  builderFields: FormlyFieldConfig[] = [];
  builderModel: IExpressionBuilderModel = { rules: [] };
  // hideBuilderWrapper => single label per control (no duplicate builder header)
  builderOptions: FormlyFormOptions = {
    formState: { hideBuilderWrapper: true },
  };

  // Expression preview
  generatedExpressions: Record<string, string> = {};

  // Inline tester
  testModel = '{}';
  testFormState = '{}';
  testResults: { key: string; result: any; error?: string }[] = [];

  // Dynamic targets based on field type
  private dynamicTargets: IExpressionTarget[] = [];

  // Expressions that couldn't be parsed into visual builder rules
  private unparsedExpressions: Record<string, string> = {};

  private formBuilderService: FormBuilderService;
  private observer: MutationObserver | null = null;
  private cleanupListeners: (() => void)[] = [];

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    this.formBuilderService = this.injector.get(FormBuilderService);
  }

  ngOnInit(): void {
    this.loadBuilderForm();
  }

  ngAfterViewInit(): void {
    this.setupMutationObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['field'] && this.field) {
      this.loadBuilderForm();
      this.loadFieldExpressions();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.cleanupListeners.forEach(fn => fn());
    this.cleanupListeners = [];
  }

  // --- Visual Builder ---

  onBuilderModelChange(): void {
    if (!this.field) return;
    try {
      this.applyAllExpressions();
    } catch (e) {
      console.error('Failed to build expressions:', e);
    }
  }

  // --- Inline Tester ---

  populateTestData(): void {
    const allExprs = this.getAllExpressions();
    const model: Record<string, any> = {};
    const formState: Record<string, any> = {};

    // Build nested skeletons from the FULL path each expression references
    // (e.g. model?.SECTION?.BLOCK?.PASSPORT_NUMBER), so the sample value lands
    // exactly where the expression reads it. A shallow first-segment-only sample
    // leaves the real value undefined, so every rule evaluated to the same result.
    for (const expr of Object.values(allExprs)) {
      for (const keys of this.extractPaths(expr, 'model')) {
        this.setSkeletonPath(model, keys);
      }
      for (const keys of this.extractPaths(expr, 'formState')) {
        this.setSkeletonPath(formState, keys);
      }
    }

    this.testModel = Object.keys(model).length
      ? JSON.stringify(model, null, 2)
      : '{}';
    this.testFormState = Object.keys(formState).length
      ? JSON.stringify(formState, null, 2)
      : '{}';
    this.testResults = [];
    this.cdr.markForCheck();
  }

  onTestModelChange(value: string): void {
    this.testModel = value;
    this.invalidateTestResults();
  }

  onTestFormStateChange(value: string): void {
    this.testFormState = value;
    this.invalidateTestResults();
  }

  /** Editing a sample input makes the shown results stale — clear them so the user re-runs. */
  private invalidateTestResults(): void {
    if (this.testResults.length) {
      this.testResults = [];
      this.cdr.markForCheck();
    }
  }

  runTests(): void {
    this.testResults = [];
    const allExpressions = this.getAllExpressions();
    try {
      const model = JSON.parse(this.testModel);
      const formState = JSON.parse(this.testFormState);
      // Mirror the runtime shape expressions rely on so value/validity chains
      // resolve instead of silently returning undefined. Generated expressions
      // reach the root model by walking up the field tree a variable number of
      // hops (e.g. field.parent.parent.parent.parent.model), so make `parent`
      // self-referential — any depth of `.parent….model` resolves to the model.
      const parentCtx: any = { model, options: { formState } };
      parentCtx.parent = parentCtx;
      const fieldCtx: any = {
        ...(this.field || {}),
        model,
        formState,
        parent: parentCtx,
        options: { formState },
        form: this.toFormProxy(model),
        props: { ...(this.field?.props || {}) },
      };

      // Evaluate `hide` first, then feed every evaluated result back into the
      // field context. This mirrors ngx-formly: a rule like `props.required`
      // that references `field.hide` must see the hide value computed from the
      // current sample — not the field's static config value.
      // 'hide' must be evaluated before every other expression (rank 0 vs 1).
      const hideFirst = (key: string): number => (key === 'hide' ? 0 : 1);
      const entries = Object.entries(allExpressions).sort(
        ([a], [b]) => hideFirst(a) - hideFirst(b)
      );
      for (const [key, expr] of entries) {
        try {
          const fn = new Function(
            'model',
            'formState',
            'field',
            `return (${expr})`
          );
          const result = fn(model, formState, fieldCtx);
          this.testResults.push({ key, result });
          this.assignToPath(fieldCtx, key, result);
        } catch (e: any) {
          this.testResults.push({ key, result: null, error: e.message });
        }
      }
    } catch (e: any) {
      this.testResults.push({
        key: 'parse',
        result: null,
        error: `Invalid JSON: ${e.message}`,
      });
    }
    this.cdr.markForCheck();
  }

  /** Pull the property key chains that follow a given root (`model`/`formState`). */
  private extractPaths(expr: string, root: 'model' | 'formState'): string[][] {
    const paths: string[][] = [];
    const chainRe = new RegExp(
      `\\b${root}((?:\\s*\\??\\.\\s*[A-Za-z_$][\\w$]*|\\s*\\[\\s*['"][^'"\\]]+['"]\\s*\\])+)`,
      'g'
    );
    let match: RegExpExecArray | null;
    while ((match = chainRe.exec(expr)) !== null) {
      const chain = match[1];
      const keyRe = /\??\.\s*([A-Za-z_$][\w$]*)|\[\s*['"]([^'"\]]+)['"]\s*\]/g;
      const keys: string[] = [];
      let km: RegExpExecArray | null;
      while ((km = keyRe.exec(chain)) !== null) {
        const key = km[1] || km[2];
        if (key === 'includes') break; // stop at helper calls (e.g. .includes())
        if (key) keys.push(key);
      }
      if (keys.length) paths.push(keys);
    }
    return paths;
  }

  /** Assign an evaluated result onto the field context at a dotted target (e.g. props.required). */
  private assignToPath(obj: any, path: string, value: any): void {
    const parts = path.split('.');
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (typeof cur[parts[i]] !== 'object' || cur[parts[i]] === null) {
        cur[parts[i]] = {};
      }
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
  }

  /** Create nested placeholders for a key chain, converting leaves to objects as needed. */
  private setSkeletonPath(root: Record<string, any>, keys: string[]): void {
    let cur = root;
    keys.forEach((key, i) => {
      const isLast = i === keys.length - 1;
      if (isLast) {
        if (!(key in cur)) cur[key] = '';
      } else {
        if (typeof cur[key] !== 'object' || cur[key] === null) cur[key] = {};
        cur = cur[key];
      }
    });
  }

  /** Minimal FormControl-like proxy over the model for get()/value/valid chains. */
  private toFormProxy(value: any): any {
    const isObj = value !== null && typeof value === 'object';
    return {
      value,
      valid: true,
      invalid: false,
      get: (key: string) =>
        this.toFormProxy(isObj && key in value ? value[key] : undefined),
    };
  }

  // --- DOM-based collapse for formly-rendered repeater items ---

  private setupMutationObserver(): void {
    if (!this.builderContainer?.nativeElement) return;

    // Initial setup
    setTimeout(() => this.attachCollapseHandlers(), 200);

    // Watch for DOM changes (formly re-renders when model changes)
    this.observer = new MutationObserver(() => {
      this.attachCollapseHandlers();
    });

    this.observer.observe(this.builderContainer.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private attachCollapseHandlers(): void {
    if (!this.builderContainer?.nativeElement) return;
    const container: HTMLElement = this.builderContainer.nativeElement;

    const titleElements = container.querySelectorAll(
      '.service-builder-styles > .title:not(.fb-collapse-ready)'
    );

    titleElements.forEach((titleEl: Element) => {
      titleEl.classList.add('fb-collapse-ready');

      const fieldContainer = titleEl.nextElementSibling;
      if (
        !fieldContainer ||
        !fieldContainer.classList.contains('field-container')
      )
        return;

      const unlisten = this.renderer.listen(
        titleEl,
        'click',
        (event: Event) => {
          const target = event.target as HTMLElement;
          if (target.closest('button') || target.tagName === 'BUTTON') return;

          fieldContainer.classList.toggle('fb-collapsed');
          titleEl.classList.toggle('fb-title-collapsed');
        }
      );
      this.cleanupListeners.push(unlisten);
    });
  }

  // --- Private ---

  private loadBuilderForm(): void {
    const fieldType = this.field?.type?.toString() || '';
    const fieldKey = this.field?.key?.toString() || '';
    this.dynamicTargets = getExpressionTargetsForField(fieldType, fieldKey);

    try {
      const config = getExpressionBuilderFormConfig(
        this.formBuilderService,
        this.dynamicTargets
      );
      this.builderFields = [config];
    } catch (e) {
      console.error('Failed to load expression builder form:', e);
    }
    this.cdr.markForCheck();
  }

  private loadFieldExpressions(): void {
    if (!this.field) return;

    this.builderModel = { rules: [] };
    this.unparsedExpressions = {};

    if (this.field.expressions) {
      const expressions = this.field.expressions as Record<string, any>;
      const availableFields = getAllServiceFormFieldsIncludingSelf(
        this.formBuilderService
      );

      const { rules, unparsed } = parseExpressionToRules(
        expressions,
        availableFields,
        this.dynamicTargets
      );

      if (rules.length > 0) {
        this.builderModel = { rules };
      }

      // Keep unparsed expressions (preserved when applying)
      this.unparsedExpressions = unparsed;
    }

    // Reset forms
    this.builderForm = new FormGroup({});
    this.builderOptions = { formState: { hideBuilderWrapper: true } };
    this.generatedExpressions = buildExpressionsFromVisualModel(
      this.builderModel,
      this.dynamicTargets
    );

    // Reset the inline tester — its sample values and results belong to the
    // previously selected field, whose rules differ from this one's.
    this.testModel = '{}';
    this.testFormState = '{}';
    this.testResults = [];

    this.cdr.markForCheck();
  }

  private applyAllExpressions(): void {
    if (!this.field) return;

    // Build expressions from visual builder
    const visualExpressions = buildExpressionsFromVisualModel(
      this.builderModel,
      this.dynamicTargets
    );
    this.generatedExpressions = visualExpressions;

    // Merge with unparsed expressions (preserved from load)
    const allExpressions: Record<string, string> = {
      ...this.unparsedExpressions,
      ...visualExpressions,
    };

    // Preserve non-string expressions (function expressions from formly config)
    const existing = (this.field.expressions as Record<string, any>) || {};
    const preserved: Record<string, any> = {};
    for (const [key, value] of Object.entries(existing)) {
      if (typeof value !== 'string') {
        preserved[key] = value;
      }
    }

    // Apply to field
    this.field.expressions = { ...preserved, ...allExpressions };

    this.expressionsChanged.emit();
    this.cdr.markForCheck();
  }

  private getAllExpressions(): Record<string, string> {
    const visualExpressions = buildExpressionsFromVisualModel(
      this.builderModel,
      this.dynamicTargets
    );
    return { ...this.unparsedExpressions, ...visualExpressions };
  }
}
